import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import clsx from 'clsx';
import { ChevronRight, Toolbox } from '@/components/icons';
import { useAuth } from '@/lib/auth';
import { Badge, Card, EmptyState, PageHeader } from '@/components/ui';
import { HELPER_SERVICES, usableHelperServices } from '@/features/helpers/services';

/**
 * Helper services (/helpers): the tools that run beside 1QA, one sub-page each (features/helpers/services.ts). The
 * sub-pages share a tab strip back to the overview and to each other.
 */
export function HelperServicesLayout() {
  const { can } = useAuth();
  const services = usableHelperServices(can);
  return (
    <div className="space-y-6">
      <nav aria-label="Helper services" className="flex w-full overflow-x-auto shadow-[inset_0_-1px_0_var(--color-border)]">
        {[{ path: '', label: 'Overview' }, ...services].map((s) => (
          <NavLink
            key={s.path}
            to={s.path ? `/helpers/${s.path}` : '/helpers'}
            end
            className={({ isActive }) =>
              clsx(
                'inline-flex shrink-0 items-center gap-2 border-b-2 px-6 py-4 text-xs font-semibold tracking-label whitespace-nowrap uppercase transition',
                isActive ? 'border-primary text-fg' : 'border-transparent text-fg-muted hover:text-fg-secondary',
              )
            }
          >
            {s.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

/** The overview: every helper service, what it is for, and the ones still to come. */
export function HelperServicesPage() {
  const { can } = useAuth();
  const usable = new Set(usableHelperServices(can).map((s) => s.id));
  const shown = HELPER_SERVICES.filter((s) => usable.has(s.id) || !s.available);
  return (
    <div>
      <PageHeader
        eyebrow="Tools"
        title="Helper services"
        description="Tools that work beside 1QA: record web tests in your browser, connect real phones for mobile tests, and more to come for security and performance testing."
      />
      {usable.size === 0 ? (
        <EmptyState icon={<Toolbox className="size-6" />} title="No helper services for your role" description="Ask a Project Admin if you need the browser recorder or connected devices." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {shown.map((s) => {
            const Icon = s.icon;
            const body = (
              <div className="flex items-start gap-3">
                  <span className={clsx('grid size-10 shrink-0 place-items-center rounded-card', s.available ? 'bg-primary-subtle text-primary' : 'bg-surface-sunken text-fg-muted')}>
                    <Icon className="size-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-fg">{s.label}</span>
                      <Badge tone="muted">{s.area}</Badge>
                      {!s.available && <Badge tone="pending">Coming later</Badge>}
                    </div>
                    <p className="mt-1 text-sm text-fg-secondary">{s.description}</p>
                  </div>
                  {s.available && <ChevronRight className="mt-1 size-4 shrink-0 text-fg-muted" aria-hidden />}
              </div>
            );
            return s.available ? (
              <Link key={s.id} to={`/helpers/${s.path}`} data-testid={`helper-${s.id}`} className="block rounded-card focus-visible:outline-2 focus-visible:outline-primary">
                <Card interactive className="h-full p-5">
                  {body}
                </Card>
              </Link>
            ) : (
              <Card key={s.id} className="h-full p-5 opacity-70" data-testid={`helper-${s.id}`} aria-disabled>
                {body}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Old addresses (/recorder, /devices) keep working: they open the sub-page with the same query (e.g. ?project=). */
export function HelperRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`/helpers/${to}${search}${hash}`} replace />;
}
